import React, { useState } from 'react';
import { X, Sparkles, Loader } from 'lucide-react';
import { Slide } from '../types';
import { QUICK_PROMPTS } from '../constants/slidePrompts';

interface SlidePromptPanelProps {
  slide: Slide;
  onRegenerate: (prompt: string, objectType?: 'text' | 'image' | 'chart') => Promise<void>;
  onClose: () => void;
  language: 'ko' | 'en';
  isDarkMode?: boolean;
}

export const SlidePromptPanel: React.FC<SlidePromptPanelProps> = ({
  slide,
  onRegenerate,
  onClose,
  language,
  isDarkMode = false,
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedObjectType, setSelectedObjectType] = useState<'text' | 'image' | 'chart' | undefined>(undefined);

  const translations = {
    ko: {
      title: '슬라이드 내용 재생성',
      promptPlaceholder: '어떻게 내용을 수정하고 싶으신가요?',
      regenerate: '재생성',
      close: '닫기',
      quickPrompts: '간단 프롬프트',
      generating: '생성 중...',
      objectType: '생성할 객체 유형',
      textObject: '텍스트',
      imageObject: '이미지',
      chartObject: '차트',
      noTypeSelected: '유형 선택 안함',
    },
    en: {
      title: 'Regenerate Slide Content',
      promptPlaceholder: 'How would you like to modify the content?',
      regenerate: 'Regenerate',
      close: 'Close',
      quickPrompts: 'Quick Prompts',
      generating: 'Generating...',
      objectType: 'Object Type to Generate',
      textObject: 'Text',
      imageObject: 'Image',
      chartObject: 'Chart',
      noTypeSelected: 'No Type Selected',
    },
  };

  const t = translations[language];

  const quickPrompts = QUICK_PROMPTS[language];

  const handleRegenerate = async (customPrompt?: string) => {
    const finalPrompt = customPrompt || prompt;
    if (!finalPrompt.trim() && !customPrompt) {return;}

    setIsGenerating(true);
    try {
      await onRegenerate(finalPrompt, selectedObjectType);
      if (!customPrompt) {
        setPrompt('');
      }
    } catch (error) {
      console.error('Failed to regenerate slide:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`absolute right-0 top-0 w-80 h-full ${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } border-l shadow-xl z-50 flex flex-col`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t.title}
          </h3>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors ${
            isDarkMode 
              ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
              : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
          }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-4 overflow-y-auto">
        {/* Current slide info */}
        <div className={`p-3 rounded-lg ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-xs font-medium mb-1 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            슬라이드 {slide.order + 1}
          </div>
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {slide.title}
          </div>
        </div>

        {/* Quick prompts */}
        <div>
          <h4 className={`text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {t.quickPrompts}
          </h4>
          <div className="space-y-2">
            {quickPrompts.map(qp => (
              <button
                key={qp.id}
                onClick={() => handleRegenerate(qp.prompt)}
                disabled={isGenerating}
                className={`w-full p-2 text-sm rounded-lg text-left transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:bg-gray-800'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {qp.label}
              </button>
            ))}
          </div>
        </div>

        {/* Object Type Selection */}
        <div>
          <h4 className={`text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {t.objectType}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setSelectedObjectType(undefined)}
              className={`p-2 text-sm rounded-lg transition-colors ${
                selectedObjectType === undefined
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {t.noTypeSelected}
            </button>
            <button
              onClick={() => setSelectedObjectType('text')}
              className={`p-2 text-sm rounded-lg transition-colors ${
                selectedObjectType === 'text'
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              📝 {t.textObject}
            </button>
            <button
              onClick={() => setSelectedObjectType('image')}
              className={`p-2 text-sm rounded-lg transition-colors ${
                selectedObjectType === 'image'
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              🖼️ {t.imageObject}
            </button>
            <button
              onClick={() => setSelectedObjectType('chart')}
              className={`p-2 text-sm rounded-lg transition-colors ${
                selectedObjectType === 'chart'
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              📊 {t.chartObject}
            </button>
          </div>
        </div>

        {/* Custom prompt */}
        <div className="space-y-2">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t.promptPlaceholder}
            disabled={isGenerating}
            className={`w-full h-32 p-3 rounded-lg resize-none text-sm ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            } border focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50`}
          />
          <button
            onClick={() => handleRegenerate()}
            disabled={isGenerating || !prompt.trim()}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              isGenerating || !prompt.trim()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {isGenerating ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {t.generating}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {t.regenerate}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};